import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { ErrorResponse } from '../../models/error.model';
import { Message } from '../../models/message.model';
import { Company } from '../../models/company.model';
import { Contact, ContactResponse, ContactsResponse } from '../../models/contact.model';
import { authToken } from '../../scripts/authentication.script';
import { extractBase64 } from '../../scripts/extract.base.64.script';
import { currencyObject } from '../../libraries/currency.library';

export class ApiDigitalAdminContact {
  static apiResponse: ApiResponse

  public static async getContacts(
    id_company: string,
    contact_name: string,
    contact_country: string,
    contact_type: string,
    sort: string,
    page: number,
    limit: number,
    signal?: AbortSignal,
    is_user?: boolean
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let contactsResponse: ContactsResponse | null = null
      let errorResponse: ErrorResponse | null = null

      await axios.get('../../../../../database/contacts.data.json').then(response => { contactsResponse = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { errorResponse = response.data }).catch(error => { alert(error) })

      if (contactsResponse!.success && contactsResponse!.data.items.every(item => item.id_company === id_company)) {
        contactsResponse!.data.pagination = { page: page, limit: limit, total: contactsResponse!.data.items.length, pages: Math.ceil(contactsResponse!.data.items.length / limit) || 1 }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: contactsResponse!.success === true ? contactsResponse!.data : null
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: errorResponse!.success === false ? errorResponse!.error : null
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/contacts?name=${encodeURIComponent(contact_name)}&country=${encodeURIComponent(contact_country)}&type=${encodeURIComponent(contact_type)}&sort=${sort}&page=${page}&limit=${limit}${is_user !== undefined ? `&is_user=${is_user}` : ''}`

      let config: any = {
        headers: { Authorization: authToken() },
        signal
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        if (axios.isCancel(error)) {
          this.apiResponse = { status: 0, message: 'cancelled', data: null }
          return
        }
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getContact(
    id_company: string,
    id_contact: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let contactResponsePerson: ContactResponse | null = null
      let contactResponseCompany: ContactResponse | null = null
      let errorResponse: ErrorResponse | null = null

      await axios.get('../../../../../database/contact.person.data.json').then(response => { contactResponsePerson = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/contact.company.data.json').then(response => { contactResponseCompany = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { errorResponse = response.data }).catch(error => { alert(error) })

      if (contactResponsePerson!.data.id === id_contact || contactResponseCompany!.data.id === id_contact) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: (contactResponsePerson!.success === true && contactResponseCompany!.success === true) ? (id_contact === contactResponsePerson!.data.id) ? contactResponsePerson!.data : contactResponseCompany!.data : null
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: errorResponse!.success === false ? errorResponse!.error : null
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/contacts/${id_contact}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postContact(
    id_company: string,
    type: string,
    uuid: string,
    name: string,
    first_name: string,
    last_name: string,
    logo: any,
    email: string,
    phone: string,
    website: string,
    register_number: string,
    vat_number: string,
    street: string,
    street_number: string,
    additional: string,
    administrative_area: string,
    city: string,
    zip_code: string,
    country: string,
    currency_code: string,
    bank_account_name: string,
    bank_account_iban: string,
    bank_account_bban: string,
    bank_account_bic: string,
    language: string,
    qr_type: string,
    company_ids: string[]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let contactResponse: Contact | null = null
      let errorResponse: ErrorResponse | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { errorResponse = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        contactResponse = {
          id: `contact${(Math.floor(Math.random() * 1000)).toString()}`,
          id_company: id_company,
          type: type,
          data: {
            contact_code: (Math.floor(Math.random() * 90000000 + 10000000)).toString(),
            uuid: uuid,
            name: name,
            first_name: first_name,
            last_name: last_name,
            logo: (logo && logo.length > 0) ? logo : '',
            avatar: '',
            email: email,
            phone: phone,
            website: website,
            register_number: register_number,
            vat_number: vat_number,
            language: language,
            qr_type: qr_type,
            currency: currencyObject(company!.language, currency_code),
            address: {
              street: street,
              number: street_number,
              additional: additional,
              administrative_area: administrative_area,
              city: city,
              zip_code: zip_code,
              country: country
            },
            bank_account: {
              name: bank_account_name,
              iban: bank_account_iban,
              bban: bank_account_bban,
              bic: bank_account_bic
            },
            companies: [id_company, ...company_ids]
          }
        }

        if (logo && typeof logo !== 'string') {
          extractBase64(logo).then((image: any) => { contactResponse!.data.logo = image.base })
        }

        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: contactResponse
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: errorResponse!.success === false ? errorResponse!.error : null
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/contacts`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = new FormData()
      data.append('uuid', uuid)
      data.append('type', type)
      data.append('phone', phone)
      data.append('email', email)
      data.append('website', website)
      data.append('street', street)
      data.append('street_number', street_number)
      data.append('additional', additional)
      data.append('administrative_area', administrative_area)
      data.append('city', city)
      data.append('zip_code', zip_code)
      data.append('country', country)
      data.append('language', language)
      data.append('qr_type', qr_type)
      data.append('company', id_company)
      data.append('bank_account_name', bank_account_name)
      data.append('bank_account_bic', bank_account_bic)
      data.append('bank_account_bban', bank_account_bban)
      data.append('bank_account_iban', bank_account_iban)

      company_ids.forEach((item) => {
        data.append('other_companies', JSON.stringify(item))
      })

      if (type === 'person') {
        data.append('first_name', first_name)
        data.append('last_name', last_name)
      } else {
        data.append('logo', logo)
        data.append('name', name)
        data.append('register_number', register_number)
        data.append('vat_number', vat_number)
        data.append('currency', currency_code)
      }

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async putContact(
    id_company: string,
    id_contact: string,
    type: string,
    name: string,
    first_name: string,
    last_name: string,
    logo: any,
    email: string,
    phone: string,
    website: string,
    register_number: string,
    vat_number: string,
    street: string,
    street_number: string,
    additional: string,
    administrative_area: string,
    city: string,
    zip_code: string,
    country: string,
    currency_code: string,
    bank_account_name: string,
    bank_account_iban: string,
    bank_account_bban: string,
    bank_account_bic: string,
    language: string,
    qr_type: string,
    company_ids: string[]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let contactResponse: Contact | null = null
      let errorResponse: ErrorResponse | null = null

      await this.getContact(id_company, id_contact).then(response => { contactResponse = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { errorResponse = response.data }).catch(error => { alert(error) })

      if (contactResponse!.id === id_contact) {
        if (name.length > 0) { contactResponse!.data.name = name }
        if (first_name.length > 0) { contactResponse!.data.first_name = first_name }
        if (last_name.length > 0) { contactResponse!.data.last_name = last_name }
        if (logo) { if (logo.length > 0) { contactResponse!.data.logo = logo } else { extractBase64(logo).then((image: any) => { contactResponse!.data.logo = image.base }) } } else { contactResponse!.data.logo = '' }
        if (email.length > 0) { contactResponse!.data.email = email }
        if (phone.length > 0) { contactResponse!.data.phone = phone }
        if (website.length > 0) { contactResponse!.data.website = website }
        if (register_number.length > 0) { contactResponse!.data.register_number = register_number }
        if (vat_number.length > 0) { contactResponse!.data.vat_number = vat_number }
        if (currency_code.length > 0) { contactResponse!.data.currency = currencyObject(contactResponse!.data.language, currency_code) }
        if (street.length > 0) { contactResponse!.data.address.street = street }
        if (street_number.length > 0) { contactResponse!.data.address.number = street_number }
        if (additional.length > 0) { contactResponse!.data.address.additional = additional }
        if (administrative_area.length > 0) { contactResponse!.data.address.administrative_area = administrative_area }
        if (city.length > 0) { contactResponse!.data.address.city = city }
        if (zip_code.length > 0) { contactResponse!.data.address.zip_code = zip_code }
        if (country.length > 0) { contactResponse!.data.address.country = country }
        if (bank_account_name.length > 0) { contactResponse!.data.bank_account.name = name }
        if (bank_account_iban.length > 0) { contactResponse!.data.bank_account.iban = bank_account_iban }
        if (bank_account_bban.length > 0) { contactResponse!.data.bank_account.bban = bank_account_bban }
        if (bank_account_bic.length > 0) { contactResponse!.data.bank_account.bic = bank_account_bic }
        if (language.length > 0) { contactResponse!.data.language = language }
        if (qr_type.length > 0) { contactResponse!.data.qr_type = qr_type }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: contactResponse
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: errorResponse!.success === false ? errorResponse!.error : null
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/contacts/${id_contact}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = new FormData()
      data.append('type', type)
      data.append('phone', phone)
      data.append('email', email)
      data.append('website', website)
      data.append('street', street)
      data.append('street_number', street_number)
      data.append('additional', additional)
      data.append('administrative_area', administrative_area)
      data.append('city', city)
      data.append('zip_code', zip_code)
      data.append('country', country)
      data.append('language', language)
      data.append('qr_type', qr_type)
      data.append('bank_account_name', bank_account_name)
      data.append('bank_account_bic', bank_account_bic)
      data.append('bank_account_bban', bank_account_bban)
      data.append('bank_account_iban', bank_account_iban)

      company_ids.forEach(item => {
        data.append('companies', JSON.stringify(item))
      })

      if (type === 'person') {
        data.append('first_name', first_name)
        data.append('last_name', last_name)
      } else {
        data.append('logo', logo)
        data.append('name', name)
        data.append('register_number', register_number)
        data.append('vat_number', vat_number)
        data.append('currency', currency_code)
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async putContactExtend(
    id_company: string,
    id_contact: string,
    company_ids: string[]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let contactResponse: Contact | null = null
      let errorResponse: ErrorResponse | null = null

      await this.getContact(id_company, id_contact).then(response => { contactResponse = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { errorResponse = response.data }).catch(error => { alert(error) })

      if (contactResponse!.id === id_contact) {
        contactResponse!.data.companies = [id_company, ...company_ids]

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: contactResponse
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: errorResponse!.success === false ? errorResponse!.error : null
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/contacts/${id_contact}/companies/changes`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data = {
        other_companies: company_ids
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async deleteContacts(
    id_company: string,
    id_contacts: string[]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let contacts: Contact[] | null = null
      let message: Message | null = null
      let errorResponse: ErrorResponse | null = null

      await axios.get('../../../../../database/contacts.data.json').then(response => { contacts = response.data.data.items }).catch(error => { alert(error) })
      await axios.get('../../../../../database/message.data.json').then(response => { message = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { errorResponse = response.data }).catch(error => { alert(error) })

      if (contacts!.some(item => id_contacts.includes(item.id))) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: message
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: errorResponse!.success === false ? errorResponse!.error : null
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/contacts`

      let config: any = {
        headers: { Authorization: authToken() },
        data: { contacts: id_contacts }
      }

      await axios.delete(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getContactAddresses(
    id_company: string,
    id_contact: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      this.apiResponse = {
        status: 200,
        message: 'Success',
        data: []
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/contacts/${id_contact}/addresses`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async postContactAddress(
    id_company: string,
    id_contact: string,
    label: string,
    type: string,
    address: { street: string, number: string, additional: string, administrative_area: string, city: string, zip_code: string, country: string },
    is_default_delivery: boolean
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      this.apiResponse = {
        status: 201,
        message: 'Success',
        data: { id: `addr_${Math.floor(Math.random() * 1000)}`, label, type, address, is_default_delivery }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/contacts/${id_contact}/addresses`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        label: label,
        type: type,
        address: address,
        is_default_delivery: is_default_delivery
      }

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async putContactAddress(
    id_company: string,
    id_contact: string,
    id_address: string,
    label: string,
    type: string,
    address: { street: string, number: string, additional: string, administrative_area: string, city: string, zip_code: string, country: string },
    is_default_delivery: boolean
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      this.apiResponse = {
        status: 200,
        message: 'Success',
        data: { id: id_address, label, type, address, is_default_delivery }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/contacts/${id_contact}/addresses/${id_address}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        label: label,
        type: type,
        address: address,
        is_default_delivery: is_default_delivery
      }

      await axios.put(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async deleteContactAddress(
    id_company: string,
    id_contact: string,
    id_address: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      this.apiResponse = {
        status: 200,
        message: 'Success',
        data: { message: 'Address deleted' }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/contacts/${id_contact}/addresses/${id_address}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.delete(url, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async putContactAddressDefault(
    id_company: string,
    id_contact: string,
    id_address: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      this.apiResponse = {
        status: 200,
        message: 'Success',
        data: { message: 'Default address set' }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/contacts/${id_contact}/addresses/${id_address}/default`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.put(url, null, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }
}
